import { useRef, useState } from 'react'
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  workflowChangeSetsApi,
  type ChangeSetItemDecision,
  type ChangeSetRoundDecision,
  type WorkflowRunChangeSets,
} from '@/api/workflowChangeSets'
import { isReviewableRound, stagedRoundOf } from '@/lib/changeSetRoundDiff'
import { useTenantKey } from './useTenantKey'

export function useWorkflowRunChangeSets(projectId: string, runId: string | null, enabled = true) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'change-sets'),
    queryFn: () => workflowChangeSetsApi.getChangeSets(projectId, runId!),
    enabled: !!projectId && !!runId && enabled,
    staleTime: 2_000,
  })
}

function withDecision(
  data: WorkflowRunChangeSets,
  changeSetId: string,
  decision: ChangeSetItemDecision,
  itemId?: string,
): WorkflowRunChangeSets {
  const target = data.changeSets.find((c) => c.id === changeSetId)
  return {
    ...data,
    changeSets: data.changeSets.map((c) => {
      if (c.id === changeSetId)
        return { ...c, items: c.items.map((i) => (itemId === undefined || i.id === itemId ? { ...i, decision } : i)) }
      if (decision === 'Staged' && target && c.producingPhaseId === target.producingPhaseId && isReviewableRound(data.changeSets, c))
        return { ...c, items: c.items.map((i) => (i.decision === 'Staged' ? { ...i, decision: 'Unstaged' as const } : i)) }
      return c
    }),
  }
}

function useOptimisticDecision<TVariables>(
  projectId: string,
  runId: string,
  mutationFn: (variables: TVariables) => Promise<unknown>,
  apply: (data: WorkflowRunChangeSets, variables: TVariables) => WorkflowRunChangeSets,
) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  const queryKey = tenantKey('projects', projectId, 'workflow-runs', runId, 'change-sets')
  const mutationKey = tenantKey('projects', projectId, 'change-set-decisions', runId)
  return useMutation({
    mutationKey,
    mutationFn,
    onMutate: async (variables: TVariables) => {
      await queryClient.cancelQueries({ queryKey })
      queryClient.setQueryData<WorkflowRunChangeSets>(queryKey, (data) => data && apply(data, variables))
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey }) === 1) queryClient.invalidateQueries({ queryKey })
    },
  })
}

export function useSetChangeSetItemDecision(projectId: string, runId: string) {
  return useOptimisticDecision(
    projectId,
    runId,
    ({ changeSetId, itemId, decision }: { changeSetId: string; itemId: string; decision: ChangeSetItemDecision }) =>
      workflowChangeSetsApi.setItemDecision(projectId, runId, changeSetId, itemId, decision),
    (data, { changeSetId, itemId, decision }) => withDecision(data, changeSetId, decision, itemId),
  )
}

export function useSetChangeSetRoundDecision(projectId: string, runId: string) {
  return useOptimisticDecision(
    projectId,
    runId,
    ({ changeSetId, decision }: { changeSetId: string; decision: ChangeSetRoundDecision }) =>
      workflowChangeSetsApi.setRoundDecision(projectId, runId, changeSetId, decision),
    (data, { changeSetId, decision }) => withDecision(data, changeSetId, decision),
  )
}

export function useEditChangeSetItemProposedContent(projectId: string, runId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ changeSetId, itemId, proposedValuesJson }: { changeSetId: string; itemId: string; proposedValuesJson: string }) =>
      workflowChangeSetsApi.editItemProposedContent(projectId, runId, changeSetId, itemId, proposedValuesJson),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'change-sets') })
      toast.success('Changes saved.')
    },
  })
}

export function useSubmitGateFeedback(projectId: string, runId: string | null) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (message: string) => workflowChangeSetsApi.submitGateFeedback(projectId, runId!, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
    },
  })
}

export function useNothingStagedApprovalGuard(projectId: string, runId: string | null) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  const [pendingApproval, setPendingApproval] = useState<{ approve: () => void; checkFailed: boolean } | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const checking = useRef(false)
  const decisionsKey = (id: string | null) => tenantKey('projects', projectId, 'change-set-decisions', id)
  const savingDecisions = useIsMutating({ mutationKey: decisionsKey(runId) }) > 0

  const guardApproval = async (runId: string, approve: () => void) => {
    if (checking.current || queryClient.isMutating({ mutationKey: decisionsKey(runId) }) > 0) return
    checking.current = true
    setIsChecking(true)
    try {
      const data = await queryClient.fetchQuery({
        queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'change-sets'),
        queryFn: () => workflowChangeSetsApi.getChangeSets(projectId, runId),
        staleTime: 0,
      })
      const pending = data.changeSets.find((c) => c.status === 'Pending')
      if (pending && !stagedRoundOf(data.changeSets)) {
        setPendingApproval({ approve, checkFailed: false })
        return
      }
      approve()
    } catch {
      setPendingApproval({ approve, checkFailed: true })
    } finally {
      checking.current = false
      setIsChecking(false)
    }
  }

  return {
    guardApproval,
    isChecking: isChecking || savingDecisions,
    reason: savingDecisions ? 'Saving decisions…' : undefined,
    confirmOpen: pendingApproval !== null,
    checkFailed: pendingApproval?.checkFailed ?? false,
    confirm: () => {
      pendingApproval?.approve()
      setPendingApproval(null)
    },
    cancel: () => setPendingApproval(null),
  }
}

